import { CityMap } from "./map";
import { Sidebar } from "./sidebar";

export class Widget {
    private element: HTMLElement;
    private drawer: HTMLElement | null = null;
    private buttons: HTMLElement[];
    private widgetLatchIcon: HTMLElement;
    private widgetLatch: HTMLElement | null;
    private widgetBase: HTMLElement | null;

    constructor(selector: string) {
        const element = document.querySelector<HTMLElement>(selector);
        if (!element) throw new Error(`Element with selector "${selector}" not found`);
        this.element = element;
        this.drawer = this.element.querySelector('.drawer');
        this.buttons = Array.from(this.element.querySelectorAll('.widget-button'));
        this.widgetLatchIcon = this.element.querySelector('.widget-latch i') as HTMLElement;
        this.widgetLatch = this.element.querySelector('.widget-latch');
        this.widgetBase = this.element.querySelector('.widget-base');
        this.updateWidgetWidth();
    }

    private updateWidgetWidth(): void {
        if (!this.widgetBase) return;

        const baseButtons = this.widgetBase.querySelectorAll('.widget-button');
        const buttonCount = baseButtons.length;

        if (buttonCount > 0) {
            // from CSS: button width 71px, gap 2px, first-child margin-left 5px, last-child margin-right 5px
            const buttonWidth = 71;
            const gap = 2;
            const sideMargins = 10; // 5px left + 5px right
            const newWidth = buttonCount * buttonWidth + (buttonCount - 1) * gap + sideMargins;
            this.element.style.setProperty('--widget-width', `${newWidth}px`);
        }
    }

    public toggleFilterButton(filterGroup: string, active: boolean): void {
        const button = this.element.querySelector<HTMLElement>(
            `.widget-button[data-filtergroups="${filterGroup}"]`
        );
        
        if (button) {
            // Simulate a click event instead of just toggling the class
            if (active && !button.classList.contains('active')) {
                button.click();
            }
        }
    }

    public toggleDrawer(): void {
        if (this.isDrawerOpen()) {
            this.closeDrawer();
        } else {
            this.openDrawer();
        }
    }

    public openDrawer(): void {
        const baseButtonCount = this.widgetBase?.querySelectorAll('.widget-button').length ?? 0;
        const buttonsPerRow = baseButtonCount > 0 ? baseButtonCount : 4;
        const totalButtonCount = this.buttons.length;
        const drawerButtonCount = totalButtonCount - baseButtonCount;

        const rows = Math.ceil(drawerButtonCount / buttonsPerRow);
        if (getComputedStyle(document.documentElement).getPropertyValue('--widget-drawer-rows') === '') {
            if (rows > 0) {
                document.documentElement.style.setProperty('--widget-drawer-rows', rows.toString());
            }
        }

        this.drawer?.classList.toggle('open', true);
        this.widgetLatchIcon.classList.toggle('fa-caret-up', false);
        this.widgetLatchIcon.classList.toggle('fa-caret-down', true);
    }

    public closeDrawer(): void {
        this.widgetLatchIcon.classList.toggle('fa-caret-up', true);
        this.widgetLatchIcon.classList.toggle('fa-caret-down', false);
        this.drawer?.classList.toggle('open', false);
    }

    public isDrawerOpen(): boolean {
        return this.drawer?.classList.contains('open') || false;
    }

    public addToggleHandler(querySelector: string): void {
        const toggleButton: HTMLElement = document.querySelector(querySelector) as HTMLElement;
        toggleButton.addEventListener('click', () => this.toggleDrawer());
    }

    public addOpenHandler(querySelector: string): void {
        const openButton: HTMLElement = document.querySelector(querySelector) as HTMLElement;
        openButton.addEventListener('click', () => this.openDrawer());
    }

    public addCloseHandler(querySelector: string): void {
        const closeButton: HTMLElement = document.querySelector(querySelector) as HTMLElement;
        closeButton.addEventListener('click', () => this.closeDrawer());
    }

    public dodgeSidebar(dodge: boolean): void {
        this.element.classList.toggle('dodge-sidebar', dodge);
    }

    public attachLatchClickHandler(): void {
        this.widgetLatch?.addEventListener('click', () => {
            this.toggleDrawer();
        });

        // event listener on widget base to open drawer when scrolled up on widget base
        this.widgetBase?.addEventListener('wheel', (event: WheelEvent) => {
            // mouse wheel up
            if (event.deltaY < 0) {
                this.openDrawer();
            }
            // mouse wheel down
            if (event.deltaY > 0) {
                this.closeDrawer();
            }
        });
    }

    public toggleButton(button: HTMLDivElement, force: boolean | null = null): boolean {
        if (!button) return false;

        if (!button.classList.contains('active') && button.parentElement?.id == 'widget-drawer') {
            this.openDrawer();
        }

        if (force !== null) {
            button.classList.toggle('active', force);
            return true;
        }

        button.classList.toggle('active');
        return true;
    }

    public toggleFilterButtonFromSidebar($sidebarName: string, force: boolean | null = null) {
        const filterButton = document.querySelector(`.widget-button[data-sidebar="${$sidebarName}"]`);
        this.toggleButton(filterButton as HTMLDivElement, force);
    }
}